import { CommonModule } from '@angular/common';
import {
  Component, ChangeDetectionStrategy, Input, Output, EventEmitter, HostListener, ElementRef,
  ChangeDetectorRef, OnChanges, SimpleChanges,
  AfterViewInit
} from '@angular/core';
import isEqual from 'lodash-es/isEqual';

import {
  DropDownListDefaultValues, DropDownListOption
} from '@design-system/components/templates/drop-down-list/entities/drop-down-list.types';
import { InputTextFailedType } from '@design-system/components/molecules/input-text/entities/input-text.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  DropDownFieldEvent, DropDownFieldType
} from '@design-system/components/templates/drop-down-field/entities/drop-down-field.types';
import { InputTextDsComponent } from '@design-system/components/molecules/input-text/input-text.component';
import { DropDownListDsComponent } from '@design-system/components/templates/drop-down-list/drop-down-list.component';

@Component({
  selector: 'app-ds-drop-down-field',
  templateUrl: './drop-down-field.component.html',
  styleUrls: ['./drop-down-field.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    InputTextDsComponent,
    DropDownListDsComponent,
  ]
})
export class DropDownFieldDsComponent implements OnChanges, AfterViewInit {

  @Input() type = DropDownFieldType.FieldList;
  @Input() isDisabled = false;
  @Input() dropDownListSelectedBy = DropDownListDefaultValues.SelectedBy;
  @Input() dropDownListOptions: DropDownListOption[] = [];
  @Input() dropDownListSelected: DropDownListOption;
  @Input() fieldFailedType = InputTextFailedType.Error;
  @Input() fieldFailedMessage: string;
  @Input() fieldPlaceholder: string;
  @Input() fieldLabel: string;
  @Input() fieldIsFailed = false;
  @Input() fieldIsFilled = false;
  @Input() fieldStaticFilled = false;
  @Input() dropDownListSearchInput = false;
  @Input() dropDownListSearchInputPlaceholder = '';
  @Input() infiniteScrollDisabled = true;

  @Output() fieldEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() selectEvent: EventEmitter<DropDownFieldEvent> = new EventEmitter<DropDownFieldEvent>();

  @HostListener('document:click', ['$event']) onDocumentClick(e: MouseEvent): void {
    const targetElement = e?.target as HTMLElement ?? null;
    if (
      this.type === DropDownFieldType.FieldList && !this.elementRef.nativeElement.contains(targetElement)
    ) {
      if (this.toggleFieldListHaveBeenCalled && !this.showDropDownList) {
        this.toggleFieldListHaveBeenCalled = false;
      } else if (this.toggleFieldListHaveBeenCalled && this.showDropDownList) {
        this.showDropDownList = false;
        this.toggleFieldListHaveBeenCalled = false;
      } else if (this.showDropDownList) {
        this.toggleFieldListHaveBeenCalled = true;
      }
      this.cdr.detectChanges();
    }
  }

  public showDropDownList = false;

  public height: number;

  public readonly chevronFilledDown = Icon.ChevronFilledDown;

  private toggleFieldListHaveBeenCalled = false;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes?.dropDownListOptions && !isEqual(
        changes?.dropDownListOptions.currentValue, changes?.dropDownListOptions.previousValue
      )
    ) {
      this.height = this.getHeight(changes.dropDownListOptions.currentValue?.length);
    }
  }
  
  ngAfterViewInit(): void {
    if (this.dropDownListOptions.length === 1) {
      this.dropDownListSelected = this.dropDownListOptions[0];
      this.selectEvent.emit({ Option: this.dropDownListOptions[0], Type: this.type });
    }
  }

  public selectFieldAction(): void {
    if (this.type === DropDownFieldType.FieldList) {
      this.toggleFieldList();
    } else {
      this.fieldEvent.emit();
    }
  }

  public selectFieldListAction(option: DropDownListOption): void {
    this.dropDownListSelected = option;
    this.selectEvent.emit({ Option: option, Type: this.type });
    this.toggleFieldList();
  }

  public onListClick(e: MouseEvent): void {
    e?.stopPropagation?.();
    e?.preventDefault?.();
  }

  public searched(results: DropDownListOption[]): void {
    this.height = this.getHeight(results?.length);
  }

  private toggleFieldList(): void {
    this.showDropDownList = !this.showDropDownList;
  }

  private getHeight(length: number): number {
    if (length) {
      return length >= 2 ? (this.dropDownListSearchInput ? 230 : 185) : (this.dropDownListSearchInput ? 155 : 75);
    } else {
      return this.dropDownListSearchInput ? 80 : 0;
    }
  }

}
